// Client-side bootstrap helpers shared between `joined` and
// `snapshot` handlers (Phase 5 Step 3).
//
// The `joined` envelope (Phase 2 legacy) and the `snapshot` reply from
// the Phase 5 resume handshake carry the same bootstrap fields —
// players, inventory, nodes, workshop, skills, credits, market.
// Extracted here so both reducer arms call one function and main.ts
// stays under the §10 400-line cap.
//
// applyBootstrap also owns the PlayerJoined-for-self seeding: the
// local player is in the `players` array, so we pick it out for
// `clientState.self` and align the LocalController to the
// server-persisted coordinates.

import type {
  InventorySlot,
  PlayerState,
  ResourceNode,
  SkillEntry,
  WorkshopState,
} from "./types.ts";
import type { EquippedItemReadout } from "../../shared/equip-messages.ts";
import type { MarketView } from "../../shared/market.ts";

// Dep surfaces as structural types. Narrower shape matches the concrete
// classes (Renderer / LocalController / RemoteInterpolator / InventoryUI
// / NodeRenderer / CreditsHUD) without an `import type` chain that
// would drag their DOM-using source files into the server's tsc include
// graph (server/tsconfig.json only lifts ES2022 — no DOM). Unit tests
// under server/src/ can assemble minimal stubs without depending on
// PixiJS at type time.
type RendererDep = {
  addPlayer(player: PlayerState): void;
  removePlayer(playerId: string): void;
};
type InterpDep = {
  seed(playerId: string, x: number, y: number, now: number): void;
  drop(playerId: string): void;
};
type LocalDep = { setPosition(x: number, y: number): void };
type InventoryUIDep = { update(inventory: InventorySlot[]): void };
type NodeRendererDep = { init(nodes: ResourceNode[]): void };
type CreditsHudDep = { update(credits: number): void };

// Mirrors client/src/market-ui.ts ClientMarketState by structure; the
// concrete type's other fields (like timers) aren't relevant here.
type ClientMarketStateLike = {
  listings: MarketView["listings"];
  npcInventory: MarketView["npcInventory"];
  npcPrices: MarketView["npcPrices"];
  fees: MarketView["fees"];
  priceHistories: Record<string, unknown>;
  // Phase 6 Block C: preserved across bootstrap so cached remote views survive.
  remoteHistories: Record<string, unknown>;
};

export type BootstrapPayload = {
  playerId: string;
  zoneId: string;
  players: PlayerState[];
  inventory: InventorySlot[];
  nodes: ResourceNode[];
  workshop: WorkshopState;
  skills: SkillEntry[];
  credits: number;
  market: MarketView;
  equippedItem: EquippedItemReadout | null;
};

export type BootstrapDeps = {
  localPlayerId: string;
  now: number;
  clientState: {
    self: PlayerState | null;
    players: Map<string, PlayerState>;
    inventory: InventorySlot[];
    activeGather: { nodeId: string; startedAt: number; expectedCompleteAt: number } | null;
    nodes: Map<string, ResourceNode>;
    currentZoneId: string | null;
    workshop: WorkshopState;
    skills: SkillEntry[];
    credits: number;
    market: ClientMarketStateLike;
    equippedItem: EquippedItemReadout | null;
  };
  renderer: RendererDep;
  interp: InterpDep;
  local: LocalDep;
  inventoryUI: InventoryUIDep;
  nodeRenderer: NodeRendererDep;
  creditsHud: CreditsHudDep;
};

// Hydrate client state from a full bootstrap payload. Idempotent w.r.t.
// call order — both fresh connects and resume-snapshot replies drop here.
// priceHistories is client-only and preserved across the hydrate so
// an open History tab keeps its cached series.
//
// Zone-transition handling (Phase 5 Step 5): if the snapshot reports a
// different zoneId than `clientState.currentZoneId`, wipe the zone-local
// scratch (nodes, remote players, active gather) BEFORE re-applying so
// stale entities from the previous zone never leak into the new one.
// Fresh connects have `currentZoneId === null`, so the first snapshot
// always passes through cleanly — nothing to clear, but we still set the
// field so the next transition has a baseline to compare against.
export function applyBootstrap(payload: BootstrapPayload, deps: BootstrapDeps): void {
  const zoneChanged =
    deps.clientState.currentZoneId !== null &&
    deps.clientState.currentZoneId !== payload.zoneId;
  if (zoneChanged) {
    for (const id of deps.clientState.players.keys()) {
      if (id === deps.localPlayerId) continue;
      deps.renderer.removePlayer(id);
      deps.interp.drop(id);
    }
    deps.clientState.players.clear();
    deps.clientState.nodes.clear();
    deps.clientState.activeGather = null;
  }
  deps.clientState.currentZoneId = payload.zoneId;

  for (const p of payload.players) {
    deps.renderer.addPlayer(p);
    deps.clientState.players.set(p.playerId, { ...p });
    if (p.playerId === deps.localPlayerId) {
      deps.clientState.self = { ...p };
      deps.local.setPosition(p.x, p.y);
    } else {
      deps.interp.seed(p.playerId, p.x, p.y, deps.now);
    }
  }
  deps.clientState.inventory = payload.inventory;
  deps.clientState.workshop = payload.workshop;
  deps.clientState.skills = payload.skills;
  deps.clientState.credits = payload.credits;
  deps.clientState.equippedItem = payload.equippedItem;
  deps.clientState.market = {
    ...payload.market,
    priceHistories: deps.clientState.market.priceHistories,
    remoteHistories: deps.clientState.market.remoteHistories,
  };
  deps.creditsHud.update(deps.clientState.credits);
  for (const n of payload.nodes) deps.clientState.nodes.set(n.nodeId, { ...n });
  deps.inventoryUI.update(payload.inventory);
  deps.nodeRenderer.init(payload.nodes);
}
